import React, {useCallback, useEffect, useState} from 'react';
import {View, StyleSheet, ScrollView, TextInput as RNTextInput} from 'react-native';
import {
  Text,
  ActivityIndicator,
  Button,
  Snackbar,
} from 'react-native-paper';
import {useTranslation} from 'react-i18next';
import {useNavigation, useRoute} from '@react-navigation/native';
import {workerRateApi} from '../../services/api/workerRateApi';
import {measurementApi} from '../../services/api/measurementApi';
import GradientHeader from '../../components/common/GradientHeader';
import {colors, spacing} from '../../theme';

const WorkerRateSetupScreen = () => {
  const {t} = useTranslation();
  const navigation = useNavigation();
  const route = useRoute();
  const {worker} = route.params;

  const [garments, setGarments] = useState([]);
  const [services, setServices] = useState([]);
  const [rates, setRates] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [snackbar, setSnackbar] = useState('');

  const buildKey = (garmentId, serviceId) => `${garmentId}_${serviceId}`;

  const fetchData = useCallback(async () => {
    try {
      const [garmentRes, serviceRes, ratesRes] = await Promise.all([
        measurementApi.listCatalogItems(false, 'GARMENT'),
        measurementApi.listCatalogItems(false, 'SERVICE'),
        workerRateApi.listByWorker(worker.id),
      ]);

      const garmentList = garmentRes.data.data || [];
      const serviceList = serviceRes.data.data || [];
      const ruleList = ratesRes.data.data || [];

      setGarments(garmentList);
      setServices(serviceList);

      const rateMap = {};
      ruleList.forEach(rule => {
        if (rule.serviceCatalogItemId) {
          const key = buildKey(rule.catalogItemId, rule.serviceCatalogItemId);
          rateMap[key] = {
            value: String(rule.rateValue),
            ruleId: rule.id,
          };
        }
      });
      setRates(rateMap);
    } catch (e) {
      setSnackbar(e.response?.data?.error?.message || t('common.error'));
    } finally {
      setIsLoading(false);
    }
  }, [worker.id, t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRateChange = useCallback((garmentId, serviceId, value) => {
    const key = buildKey(garmentId, serviceId);
    setRates(prev => ({
      ...prev,
      [key]: {...(prev[key] || {}), value},
    }));
  }, []);

  const handleSave = useCallback(async () => {
    setIsSaving(true);
    try {
      const items = [];
      for (const garment of garments) {
        for (const service of services) {
          const key = buildKey(garment.id, service.id);
          const entry = rates[key];
          const val = entry?.value?.trim();

          if (val && !isNaN(parseFloat(val)) && parseFloat(val) >= 0) {
            items.push({
              catalogItemId: garment.id,
              serviceCatalogItemId: service.id,
              rateValue: parseFloat(val),
            });
          } else {
            // Send null rateValue to delete if there was an existing rule
            items.push({
              catalogItemId: garment.id,
              serviceCatalogItemId: service.id,
              rateValue: null,
            });
          }
        }
      }

      await workerRateApi.batchSave(worker.id, items);
      setSnackbar('Rates saved successfully');
      await fetchData();
    } catch (e) {
      setSnackbar(e.response?.data?.error?.message || t('common.error'));
    } finally {
      setIsSaving(false);
    }
  }, [garments, services, rates, worker.id, fetchData, t]);

  if (isLoading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={colors.primary} />
      </View>
    );
  }

  const COL_WIDTH = 100;
  const LABEL_WIDTH = 120;

  return (
    <View style={styles.container}>
      <GradientHeader
        title={worker.fullName}
        subtitle="Worker Rates"
        onBack={() => navigation.goBack()}
      />

      {garments.length === 0 || services.length === 0 ? (
        <View style={styles.emptyContainer}>
          <Text style={styles.emptyText}>
            {garments.length === 0
              ? 'No garment catalog items found.'
              : 'No service catalog items found.'}
          </Text>
        </View>
      ) : (
        <>
          <ScrollView style={styles.scrollContainer}>
            <ScrollView horizontal showsHorizontalScrollIndicator>
              <View>
                {/* Header row */}
                <View style={styles.headerRow}>
                  <View style={[styles.cornerCell, {width: LABEL_WIDTH}]}>
                    <Text style={styles.cornerText}>Garment ↓ / Service →</Text>
                  </View>
                  {services.map(service => (
                    <View
                      key={service.id}
                      style={[styles.headerCell, {width: COL_WIDTH}]}>
                      <Text style={styles.headerText} numberOfLines={2}>
                        {service.name}
                      </Text>
                    </View>
                  ))}
                </View>

                {/* Data rows */}
                {garments.map(garment => (
                  <View key={garment.id} style={styles.dataRow}>
                    <View style={[styles.labelCell, {width: LABEL_WIDTH}]}>
                      <Text style={styles.labelText} numberOfLines={2}>
                        {garment.name}
                      </Text>
                    </View>
                    {services.map(service => {
                      const key = buildKey(garment.id, service.id);
                      const entry = rates[key];
                      return (
                        <View
                          key={service.id}
                          style={[styles.priceCell, {width: COL_WIDTH}]}>
                          <RNTextInput
                            style={styles.priceInput}
                            value={entry?.value || ''}
                            onChangeText={val =>
                              handleRateChange(garment.id, service.id, val)
                            }
                            keyboardType="numeric"
                            placeholder="—"
                            placeholderTextColor={colors.border}
                          />
                        </View>
                      );
                    })}
                  </View>
                ))}
              </View>
            </ScrollView>
          </ScrollView>

          <View style={styles.footer}>
            <Button
              mode="contained"
              onPress={handleSave}
              loading={isSaving}
              disabled={isSaving}
              style={styles.saveButton}>
              {t('common.save')}
            </Button>
          </View>
        </>
      )}

      <Snackbar
        visible={!!snackbar}
        onDismiss={() => setSnackbar('')}
        duration={3000}>
        {snackbar}
      </Snackbar>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {flex: 1, backgroundColor: colors.background},
  loadingContainer: {flex: 1, justifyContent: 'center', alignItems: 'center'},
  emptyContainer: {flex: 1, justifyContent: 'center', alignItems: 'center', padding: spacing.lg},
  emptyText: {color: colors.textSecondary, textAlign: 'center'},
  scrollContainer: {flex: 1},
  headerRow: {
    flexDirection: 'row',
    backgroundColor: colors.primary,
  },
  cornerCell: {
    padding: spacing.sm,
    justifyContent: 'center',
    borderRightWidth: 1,
    borderBottomWidth: 1,
    borderColor: 'rgba(255,255,255,0.3)',
  },
  cornerText: {
    fontSize: 11,
    color: colors.white,
    fontWeight: '600',
  },
  headerCell: {
    padding: spacing.sm,
    justifyContent: 'center',
    alignItems: 'center',
    borderRightWidth: 1,
    borderBottomWidth: 1,
    borderColor: 'rgba(255,255,255,0.3)',
  },
  headerText: {
    fontSize: 12,
    color: colors.white,
    fontWeight: '600',
    textAlign: 'center',
  },
  dataRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderColor: colors.border,
  },
  labelCell: {
    padding: spacing.sm,
    justifyContent: 'center',
    backgroundColor: colors.surface,
    borderRightWidth: 1,
    borderColor: colors.border,
  },
  labelText: {
    fontSize: 13,
    fontWeight: '500',
    color: colors.text,
  },
  priceCell: {
    padding: 4,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: colors.surface,
    borderRightWidth: 1,
    borderColor: colors.border,
  },
  priceInput: {
    width: '90%',
    height: 36,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 6,
    paddingHorizontal: 8,
    fontSize: 14,
    color: colors.text,
    textAlign: 'center',
    backgroundColor: colors.background,
  },
  footer: {
    padding: spacing.md,
    backgroundColor: colors.surface,
    borderTopWidth: 1,
    borderColor: colors.border,
  },
  saveButton: {
    backgroundColor: colors.primary,
    borderRadius: 10,
  },
});

export default WorkerRateSetupScreen;
